/**
 * Equipment Section — Character silhouette + unified equipment inventory pane
 *
 * The right-side pane shows all owned equipment by default.
 * When a slot is clicked on the silhouette, the pane filters to show only
 * eligible replacement items for that slot (excluding currently equipped
 * and buddy-used items).
 */

import { useState, useRef, useEffect } from 'react';
import { useGameStore } from '@/state';
import { EquipmentSlot } from '@/types';
import { EQUIPMENT_DATA } from '@/data';
import { SLOT_CONFIG, getActiveSlots } from '@/data/equipment/slotConfig';
import { equipmentManager } from '@/systems';
import { EquipmentSlotButton } from '@/components/equipment/EquipmentSlotButton';
import { ItemTile } from './ItemTile';
import { equipmentToTileData } from './adapters';
import { EquipmentDetailPanel } from './EquipmentDetailPanel';

/** CSS position classes for silhouette slot placement */
const SLOT_POSITIONS: Record<string, string> = {
  'top': 'absolute top-2 left-1/2 -translate-x-1/2',
  'left': 'absolute left-2 top-1/3 -translate-y-1/2',
  'right': 'absolute right-2 top-1/3 -translate-y-1/2',
  'bottom-left': 'absolute left-4 bottom-4',
  'bottom-right': 'absolute right-4 bottom-4',
};

export function EquipmentSection() {
  const equippedGear = useGameStore((state) => state.playerState.equippedGear);
  const ownedEquipment = useGameStore((state) => state.playerState.ownedEquipment);
  const playerStats = useGameStore((state) => state.playerStats);
  const [selectedSlot, setSelectedSlot] = useState<EquipmentSlot | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lastPointerType = useRef<string>('mouse');
  const [isTouchSelected, setIsTouchSelected] = useState(false);

  // Dismiss tooltip when tapping outside the active slot, dismiss filter when tapping outside section
  useEffect(() => {
    if (!selectedSlot) return;

    const handleClickOutside = (e: PointerEvent) => {
      const target = e.target as Node;

      // Hide tooltip if tap landed outside the active slot wrapper (which contains the tooltip)
      const activeSlotEl = document.querySelector(`[data-slot="${selectedSlot}"]`);
      if (activeSlotEl && !activeSlotEl.contains(target)) {
        setIsTouchSelected(false);
      }

      // Dismiss filter mode if tap landed outside the entire equipment section
      // (includes both silhouette AND inventory pane — so clicking items in filter mode still works)
      if (sectionRef.current && !sectionRef.current.contains(target)) {
        setSelectedSlot(null);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [selectedSlot]);

  const activeSlots = getActiveSlots(equippedGear);
  const availableQty = equipmentManager.getAvailableQuantities();

  const getEquippedItem = (slot: EquipmentSlot) => {
    const itemId = equippedGear[slot];
    return itemId ? EQUIPMENT_DATA[itemId] : null;
  };

  const handleSlotClick = (slot: EquipmentSlot) => {
    const newSlot = selectedSlot === slot ? null : slot;
    setSelectedSlot(newSlot);
    setSelectedItemId(null);
    setIsTouchSelected(newSlot !== null && lastPointerType.current === 'touch');
  };

  const handleEquipItem = (equipmentId: string) => {
    if (!selectedSlot) return;
    const equipment = EQUIPMENT_DATA[equipmentId];
    if (equipment.slot === selectedSlot) {
      equipmentManager.equipItem(equipmentId);
      setSelectedSlot(null);
    }
  };

  const handleUnequip = (slot: EquipmentSlot) => {
    equipmentManager.unequipItem(slot);
    setSelectedSlot(null);
    setIsTouchSelected(false);
  };

  const handleCancel = () => {
    setSelectedSlot(null);
    setIsTouchSelected(false);
  };

  const handleItemClick = (itemId: string) => {
    if (selectedSlot) {
      // Slot is active — clicking an item equips it
      handleEquipItem(itemId);
    } else {
      // No slot active — toggle detail panel
      setSelectedItemId(selectedItemId === itemId ? null : itemId);
    }
  };

  // Build inventory items — filtered when a slot is selected
  const currentEquippedId = selectedSlot ? equippedGear[selectedSlot] : null;

  const displayItems = Object.keys(ownedEquipment)
    .filter((id) => {
      const def = EQUIPMENT_DATA[id];
      if (!def) return false;

      if (selectedSlot) {
        // Filter mode: only show eligible replacements
        if (def.slot !== selectedSlot) return false;
        // Exclude the item currently equipped in this slot
        if (id === currentEquippedId) return false;
        // Exclude items with no available quantity (used by buddies etc.)
        if ((availableQty[id] || 0) <= 0) return false;
      }

      return true;
    })
    .map((id) => ({
      id,
      definition: EQUIPMENT_DATA[id],
      quantity: ownedEquipment[id].quantity,
      availableCount: availableQty[id] || 0,
      isEquipped: Object.values(equippedGear).includes(id),
    }));

  const selectedSlotMeta = selectedSlot ? SLOT_CONFIG[selectedSlot] : null;
  const isSlotEquipped = selectedSlot ? !!equippedGear[selectedSlot] : false;

  const selectedItemDef = selectedItemId ? EQUIPMENT_DATA[selectedItemId] : null;
  const selectedItemData = selectedItemId ? ownedEquipment[selectedItemId] : null;

  return (
    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 space-y-4">
      {/* Header with stats summary */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-white">⚙️ Equipment</h3>
          <p className="text-xs text-gray-400">Click a slot to change equipment</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {playerStats.fishingSpeed > 0 && (
            <span className="text-green-400">Spd +{Math.round(playerStats.fishingSpeed * 100)}%</span>
          )}
          {playerStats.catchChance > 0 && (
            <span className="text-blue-400">Catch +{Math.round(playerStats.catchChance * 100)}%</span>
          )}
          {playerStats.qualityBonus > 0 && (
            <span className="text-purple-400">Qual +{Math.round(playerStats.qualityBonus * 100)}%</span>
          )}
          {playerStats.valueBonus > 0 && (
            <span className="text-yellow-400">Val +{Math.round(playerStats.valueBonus * 100)}%</span>
          )}
        </div>
      </div>

      {/* Silhouette + Equipment inventory side by side */}
      <div className="flex flex-col lg:flex-row gap-4" ref={sectionRef}>
        {/* Left: Character silhouette with slot buttons */}
        <div className="flex justify-center lg:flex-shrink-0">
          <div className="relative w-64 h-72 bg-gray-700/30 rounded-lg border-2 border-gray-600 flex items-center justify-center">
            <div className="text-8xl opacity-20">🎣</div>

            {activeSlots.map((slot) => {
              const meta = SLOT_CONFIG[slot];
              const posClass = SLOT_POSITIONS[meta.silhouettePosition] || '';
              return (
                <div
                  key={slot}
                  data-slot={slot}
                  className={`${posClass} [&:hover]:z-50`}
                  onPointerDown={(e) => { lastPointerType.current = e.pointerType; }}
                >
                  <EquipmentSlotButton
                    slot={slot}
                    equipped={getEquippedItem(slot)}
                    isSelected={selectedSlot === slot}
                    showTooltip={selectedSlot === slot && isTouchSelected}
                    onClick={() => handleSlotClick(slot)}
                    onUnequip={() => handleUnequip(slot)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Equipment inventory / filtered picker */}
        <div className="flex-1 min-w-0">
          {/* Pane header — changes based on mode */}
          <div className="flex items-center justify-between mb-2">
            {selectedSlot && selectedSlotMeta ? (
              <>
                <h4 className="text-sm font-semibold text-gray-300">
                  Choose {selectedSlotMeta.label}:
                </h4>
                <div className="flex items-center gap-2">
                  {isSlotEquipped && (
                    <button
                      type="button"
                      onClick={() => handleUnequip(selectedSlot)}
                      className="text-xs bg-red-600/80 hover:bg-red-600 text-white px-3 py-1 rounded transition-colors"
                    >
                      Unequip
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <h4 className="text-sm font-semibold text-gray-300">📦 Your Gear</h4>
            )}
          </div>

          {/* Items grid */}
          {displayItems.length === 0 ? (
            <p className="text-xs text-gray-500 py-4">
              {selectedSlot && selectedSlotMeta
                ? `No other ${selectedSlotMeta.pluralLabel.toLowerCase()} available.`
                : 'No equipment owned yet. Visit the shop!'}
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
              {displayItems.map((item) => {
                const tileData = equipmentToTileData(item.definition, {
                  isEquipped: item.isEquipped,
                  totalOwned: item.quantity,
                });
                return (
                  <ItemTile
                    key={item.id}
                    data={tileData}
                    isSelected={!selectedSlot && selectedItemId === item.id}
                    isCompatible={!!selectedSlot}
                    onClick={() => handleItemClick(item.id)}
                  />
                );
              })}

              {/* Inline detail panel (only in browse mode, not picker mode) */}
              {!selectedSlot && selectedItemDef && selectedItemData && (
                <EquipmentDetailPanel
                  definition={selectedItemDef}
                  isEquipped={Object.values(equippedGear).includes(selectedItemId!)}
                  quantity={selectedItemData.quantity}
                  onClose={() => setSelectedItemId(null)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
